import React from 'react';
import { Dialog } from '@headlessui/react';
import { 
  XMarkIcon, 
  ServerIcon, 
  CircleStackIcon,
  CpuChipIcon,
  Square3Stack3DIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  CheckIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { HardwareConfig } from './OnPremiseMigrationEstimate';

interface HardwareConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: HardwareConfig;
  onSave: (config: HardwareConfig) => void;
}

const HardwareConfigModal: React.FC<HardwareConfigModalProps> = ({ 
  isOpen, 
  onClose, 
  config, 
  onSave 
}) => {
  const [tempConfig, setTempConfig] = React.useState<HardwareConfig>(config);

  React.useEffect(() => {
    setTempConfig(config);
  }, [config]);

  const handleSave = () => {
    onSave(tempConfig);
    onClose();
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-4xl w-full rounded-2xl bg-white dark:bg-gray-800 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="relative bg-gradient-to-br from-indigo-900 via-indigo-950 to-slate-950 px-8 py-6">
            <div className="absolute inset-0 bg-black/10"></div>
            <Dialog.Title className="relative text-2xl font-bold text-white flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                  <ServerIcon className="h-8 w-8 text-white" />
                </div>
                <div>
                  <div>Hardware Configuration</div>
                  <div className="text-sm font-normal text-white/80 mt-1">
                    Customize your on-premise infrastructure requirements
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="bg-white/10 hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </Dialog.Title>
          </div>
          
          <div className="p-8">
            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-3">
              {/* Server Config */}
              <div className="group bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900/50 dark:to-indigo-900/20 rounded-2xl p-6 border border-indigo-200 dark:border-indigo-800/50 hover:shadow-lg transition-all">
                <div className="flex items-center space-x-3 mb-5">
                  <div className="bg-indigo-100 dark:bg-indigo-900/50 p-2.5 rounded-lg">
                    <CpuChipIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Server Configuration</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Define your physical server specifications</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Number of Sockets
                    </label>
                    <select
                      value={tempConfig.sockets}
                      onChange={(e) => setTempConfig({ ...tempConfig, sockets: parseInt(e.target.value) })}
                      className="block w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:text-white transition-all"
                    >
                      <option value={1}>1 Socket</option>
                      <option value={2}>2 Sockets</option>
                      <option value={3}>3 Sockets</option>
                      <option value={4}>4 Sockets</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Cores per CPU
                    </label>
                    <select
                      value={tempConfig.coresPerCpu}
                      onChange={(e) => setTempConfig({ ...tempConfig, coresPerCpu: parseInt(e.target.value) })}
                      className="block w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:text-white transition-all"
                    >
                      <option value={16}>16 Cores</option>
                      <option value={24}>24 Cores</option>
                      <option value={32}>32 Cores</option>
                      <option value={64}>64 Cores</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      RAM per Server
                    </label>
                    <select
                      value={tempConfig.memoryPerServer}
                      onChange={(e) => setTempConfig({ ...tempConfig, memoryPerServer: parseInt(e.target.value) })}
                      className="block w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:text-white transition-all"
                    >
                      <option value={256}>256 GB</option>
                      <option value={384}>384 GB</option>
                      <option value={512}>512 GB</option>
                      <option value={768}>768 GB</option>
                      <option value={1024}>1 TB</option>
                      <option value={1280}>1.25 TB</option>
                      <option value={1536}>1.5 TB</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Storage per Server
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={tempConfig.storagePerServer}
                        onChange={(e) => setTempConfig({ ...tempConfig, storagePerServer: parseFloat(e.target.value) || 0 })}
                        className="block w-full px-4 py-3 pr-12 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:text-white transition-all"
                        placeholder="0"
                      />
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                        <span className="text-gray-500 dark:text-gray-400 font-medium">TB</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      vCPU to CPU Ratio
                    </label>
                    <select
                      value={tempConfig.vcpuToCpuRatio || 3}
                      onChange={(e) => setTempConfig({ ...tempConfig, vcpuToCpuRatio: parseFloat(e.target.value) })}
                      className="block w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:text-white transition-all"
                    >
                      <option value={1}>1:1 (No oversubscription)</option>
                      <option value={2}>2:1 (Light oversubscription)</option>
                      <option value={3}>3:1 (Standard oversubscription)</option>
                      <option value={4}>4:1 (High oversubscription)</option>
                      <option value={5}>5:1 (Very high oversubscription)</option>
                    </select>
                  </div>
                </div>
                
                {/* Total Capacity Display */}
                <div className="mt-4 p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  <div className="text-sm text-indigo-700 dark:text-indigo-300">
                    Total per server: {tempConfig.sockets * tempConfig.coresPerCpu} cores ({tempConfig.sockets * tempConfig.coresPerCpu * (tempConfig.vcpuToCpuRatio || 3)} vCPUs), {tempConfig.memoryPerServer >= 1024 ? `${(tempConfig.memoryPerServer / 1024).toFixed(2)} TB` : `${tempConfig.memoryPerServer} GB`} RAM
                  </div>
                </div>
              </div>

              {/* SAN Config */}
              <div className="group bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900/50 dark:to-purple-900/20 rounded-2xl p-6 border border-purple-200 dark:border-purple-800/50 hover:shadow-lg transition-all">
                <div className="flex items-center space-x-3 mb-5">
                  <div className="bg-purple-100 dark:bg-purple-900/50 p-2.5 rounded-lg">
                    <CircleStackIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Storage Area Network (SAN)</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Configure shared storage infrastructure</p>
                  </div>
                </div>
                
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Private SAN Cost
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <span className="text-gray-500 dark:text-gray-400">$</span>
                        </div>
                        <input
                          type="number"
                          min="0"
                          value={tempConfig.privateSanCost}
                          onChange={(e) => setTempConfig({ ...tempConfig, privateSanCost: parseInt(e.target.value) || 0 })}
                          className="block w-full pl-8 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:text-white transition-all"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Shared SAN Cost per TB
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <span className="text-gray-500 dark:text-gray-400">$</span>
                        </div>
                        <input
                          type="number"
                          min="0"
                          value={tempConfig.sharedSanCostPerTB}
                          onChange={(e) => setTempConfig({ ...tempConfig, sharedSanCostPerTB: parseInt(e.target.value) || 0 })}
                          className="block w-full pl-8 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:text-white transition-all"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <label className="flex items-start space-x-3 p-4 bg-purple-100 dark:bg-purple-900/30 rounded-xl cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-900/40 transition-colors">
                    <input
                      type="checkbox"
                      checked={tempConfig.mustUsePrivateSan}
                      onChange={(e) => setTempConfig({ ...tempConfig, mustUsePrivateSan: e.target.checked })}
                      className="mt-0.5 h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Must use Private SAN for Compliance/Policy
                      </span>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Enable this if your organization requires dedicated storage infrastructure
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Network Switches */}
              <div className="group bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-900/50 dark:to-green-900/20 rounded-2xl p-6 border border-green-200 dark:border-green-800/50 hover:shadow-lg transition-all">
                <div className="flex items-center space-x-3 mb-5">
                  <div className="bg-green-100 dark:bg-green-900/50 p-2.5 rounded-lg">
                    <Square3Stack3DIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Network Infrastructure</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Configure network switching equipment</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Cost per 48-Port Switch
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <span className="text-gray-500 dark:text-gray-400">$</span>
                    </div>
                    <input
                      type="number"
                      min="0"
                      value={tempConfig.costPer48PortSwitch}
                      onChange={(e) => setTempConfig({ ...tempConfig, costPer48PortSwitch: parseInt(e.target.value) || 0 })}
                      className="block w-full pl-8 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-green-500 focus:border-transparent dark:text-white transition-all"
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Enterprise-grade managed switches for server connectivity
                  </p>
                </div>
              </div>

              {/* Firewalls */}
              <div className="group bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900/50 dark:to-red-900/20 rounded-2xl p-6 border border-orange-200 dark:border-red-800/50 hover:shadow-lg transition-all">
                <div className="flex items-center space-x-3 mb-5">
                  <div className="bg-orange-100 dark:bg-red-900/50 p-2.5 rounded-lg">
                    <ShieldCheckIcon className="h-6 w-6 text-orange-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Security Infrastructure</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Configure firewall and security appliances</p>
                  </div>
                </div>
                
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Physical Firewall Cost
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <span className="text-gray-500 dark:text-gray-400">$</span>
                        </div>
                        <input
                          type="number"
                          min="0"
                          value={tempConfig.costPerFirewall}
                          onChange={(e) => setTempConfig({ ...tempConfig, costPerFirewall: parseInt(e.target.value) || 0 })}
                          className="block w-full pl-8 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:text-white transition-all"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Virtual Firewall Cost
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <span className="text-gray-500 dark:text-gray-400">$</span>
                        </div>
                        <input
                          type="number"
                          min="0"
                          value={tempConfig.costPerVirtualFirewall}
                          onChange={(e) => setTempConfig({ ...tempConfig, costPerVirtualFirewall: parseInt(e.target.value) || 0 })}
                          className="block w-full pl-8 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:text-white transition-all"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <label className="flex items-start space-x-3 p-4 bg-orange-100 dark:bg-red-900/30 rounded-xl cursor-pointer hover:bg-orange-200 dark:hover:bg-red-900/40 transition-colors">
                    <input
                      type="checkbox"
                      checked={tempConfig.redundantFirewalls}
                      onChange={(e) => setTempConfig({ ...tempConfig, redundantFirewalls: e.target.checked })}
                      className="mt-0.5 h-5 w-5 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Enable Redundant Firewalls
                      </span>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Deploy active/passive firewall pairs for high availability
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                <InformationCircleIcon className="h-5 w-5" />
                <span>Configuration will be saved locally</span>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2.5 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition-all transform hover:scale-105"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default HardwareConfigModal;